import { P, R } from "apparatus/library/common";
import { Arrays } from "ext/array";
import { Color, Gradient, Group, PointText, Shape } from "paper";
const WHITE = "#e0e0e0";
const BLACK = "#616161ff";
const LABEL_FOR_WHITE = "#555555";
const LABEL_FOR_BLACK = "#eeeeee";
/** List of sizes that allow white & black coloring. */
const ALLOW_WHITE_BLACK = ["twelve"];
export class SpottingTile {
    static convertToLatest(a) {
        var _a;
        return Object.assign(Object.assign(Object.assign({}, SpottingTile.properties.defaultAppearance), a), { showLabels: (_a = a.showLabels) !== null && _a !== void 0 ? _a : false });
    }
    render(appearance) {
        let wellDiameter = 20 * appearance.scale;
        let gap = 5 * appearance.scale;
        let shadowOffset = 5 * appearance.scale;
        let margin = (appearance.showLabels ? 16 : 10) * appearance.scale;
        let otherMargin = 10 * appearance.scale; // Margin only extends on the side where the labels are shown
        let rounded = 3 * appearance.scale;
        const [cols, rows] = getDimensions(appearance);
        // Create tray.
        const width = cols * wellDiameter + (cols - 1) * gap + margin + otherMargin;
        const height = rows * wellDiameter + (rows - 1) * gap + margin + otherMargin;
        let tray = Shape.Rectangle(R(-margin - wellDiameter / 2, -margin - wellDiameter / 2, width, height), rounded);
        tray.strokeColor = "#525252ff";
        tray.strokeWidth = 2.0;
        // Calculate the center of just the wells, to prevent shifting due to margins.
        let wellsCenter = P(((cols - 1) * wellDiameter + (cols - 1) * gap) / 2, ((rows - 1) * wellDiameter + (rows - 1) * gap) / 2);
        // Determine color. Note that white black is only available for 
        let finalColor = "white";
        let xIndexForBlack = 1000;
        if (appearance.color === "black") {
            finalColor = "black";
        }
        else if (appearance.color === "wb") {
            if (Arrays.includes(ALLOW_WHITE_BLACK, appearance.size)) {
                finalColor = appearance.color;
            }
            else {
                finalColor = "white";
            }
        }
        if (finalColor === "white") {
            tray.fillColor = WHITE;
        }
        else if (appearance.color === "black") {
            tray.fillColor = BLACK;
            xIndexForBlack = 0;
        }
        else if (appearance.color === "wb") {
            const gradientFillCenterStop = (wellsCenter.x - tray.bounds.left) / width;
            tray.withGradientFill("right", [
                [WHITE, gradientFillCenterStop],
                [BLACK, gradientFillCenterStop + 0.001]
            ]);
            xIndexForBlack = 2; // Preset for 12-well. Adjust if other sizes
        }
        let graphic = new Group([tray]);
        // Gradient for the wells.
        let wellGradient = new Gradient([["#f3f3f3", 0.3], ["#bbbbbb", 1.0]], true);
        let wellGradientBlack = new Gradient([["#414141ff", 0.3], ["#272727ff", 1.0]], true);
        let snapPoints = [];
        // Create wells.
        for (var i = 0; i < cols; i++) {
            for (var j = 0; j < rows; j++) {
                let well = Shape.Circle(P(0, 0), wellDiameter / 2);
                well.fillColor = new Color((i >= xIndexForBlack) ? wellGradientBlack : wellGradient, P(0, shadowOffset), P(0, wellDiameter * 1.2));
                let wellX = i * (wellDiameter + gap);
                let wellY = j * (wellDiameter + gap);
                well.position.set(wellX, wellY);
                graphic.addChild(well);
                snapPoints.push(P(wellX, wellY + 1));
            }
        }
        // Labels
        if (appearance.showLabels) {
            const fontSize = 7 * appearance.scale;
            const labelY = -wellDiameter / 2 - margin / 2;
            const labelX = -wellDiameter / 2 - margin / 2;
            // Numbers along the top
            for (let i = 0; i < cols; i++) {
                const wellX = i * (wellDiameter + gap);
                graphic.addChild(new PointText({
                    point: P(wellX, labelY + fontSize * 0.35),
                    content: (i + 1).toString(),
                    fillColor: (i >= xIndexForBlack) ? LABEL_FOR_BLACK : LABEL_FOR_WHITE,
                    fontFamily: 'sans-serif',
                    fontSize: fontSize,
                    fontWeight: 'bold',
                    justification: "center",
                }));
            }
            // Letters along the left hand side
            for (let j = 0; j < rows; j++) {
                const wellY = j * (wellDiameter + gap);
                graphic.addChild(new PointText({
                    point: P(labelX, wellY + fontSize * 0.35),
                    content: String.fromCharCode(65 + j),
                    fillColor: finalColor === "black" ? LABEL_FOR_BLACK : LABEL_FOR_WHITE,
                    fontFamily: 'sans-serif',
                    fontSize: fontSize,
                    fontWeight: 'bold',
                    justification: "center",
                }));
            }
        }
        return {
            graphic: graphic,
            hitShape: graphic.bounds.toShape(),
            pivot: wellsCenter,
            snapping: {
                type: "spotting_tile",
                at: snapPoints
            }
        };
    }
}
SpottingTile.properties = {
    label: "Spotting Tile",
    defaultAppearance: {
        size: "twelve",
        scale: 1.5,
        showLabels: false,
        color: "white",
    },
    appearanceSpecs: [
        {
            spec: "radio", key: "size", label: "Wells", options: [
                { value: "six", label: "6" },
                { value: "nine", label: "9" },
                { value: "twelve", label: "12" },
                { value: "sixteen", label: "16", tier: "boost" },
            ]
        },
        {
            spec: "slider", key: "scale", label: "Scale",
            min: 1.0, max: 2.0, step: 0.05, unit: "%",
        },
        {
            spec: "checkbox", key: "showLabels", label: "Labels", subLabel: "Show",
        },
        {
            spec: "radio", key: "color", label: "Colour", options: [
                { value: "white", label: "White" },
                { value: "black", label: "Black" },
                { value: "wb", label: "White & Black", visible: a => Arrays.includes(ALLOW_WHITE_BLACK, a.size) },
            ]
        },
    ],
};
function getDimensions(appearance) {
    switch (appearance.size) {
        case "six": return [3, 2];
        case "nine": return [3, 3];
        case "sixteen": return [4, 4];
        case "twelve": return [4, 3];
    }
}
